import React, { useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';
import WpAvatar from 'Wpds/components/avatar/WpAvatar';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpCheckbox from 'Wpds/components/checkbox/WpCheckbox';
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import DraftEditor from 'Shared/components/DraftEditor/DraftEditor';

import { getFullNameOrEmail } from 'Shared/helpers';
import { getOneOnOneWorkspacesWithBothUsers } from 'Shared/selectors/workspace';
import { duplicateItem } from 'Shared/actions/workspaces';
import { editorStateToJsonString, createEmptyEditorState, isEmptyEditor } from 'Shared/utils/draftJsHelpers';
import { getWorkspaceByUuid } from 'Shared/selectors/workspace';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import './AssignDuplicateModal.scss';

type Props = {
  onToggle: () => void;
  itemUuid: string;
  workspaceId: string;
};

const AssignDuplicateModal = ({ onToggle, itemUuid, workspaceId }: Props) => {
  const dispatch = useDispatch();
  const { tr } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editorState, setEditorState] = useState(createEmptyEditorState());
  const draftEditorEl = useRef(null);

  const { user: currentUser } = useCurrentUser();

  const workspace = useSelector(state => getWorkspaceByUuid(workspaceId)(state));
  const oneOnOneWorkspaces = useSelector(state => getOneOnOneWorkspacesWithBothUsers(state));
  const oneOnOneUsers = [
    ...oneOnOneWorkspaces.map(w => w.users.find(u => u.id != currentUser.id)),
    workspace.users.find(u => u.id === currentUser.id),
  ];
  const oneOnOneWorkspaceIdsByUser = {};
  oneOnOneWorkspaces.forEach(w => {
    const user = w.users.find(u => u.id != currentUser.id);
    oneOnOneWorkspaceIdsByUser[user.id] = w.id;
  });
  oneOnOneWorkspaceIdsByUser[currentUser.id] = workspaceId;

  const userOptions = workspace.isGroup ? workspace.users : oneOnOneUsers;
  const isOneOnOneWorkspace = workspace.isOneOnOne;
  const otherUserName = workspace.users.find(u => u.id != currentUser.id)
    ? workspace.users.find(u => u.id != currentUser.id).firstName
    : null;

  const focusTextEditor = useCallback(() => {
    if (draftEditorEl.current) draftEditorEl.current.focus();
  }, [draftEditorEl]);

  const toggleUser = userId => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length == userOptions.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(userOptions.map(u => u.id));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    (window as any).analytics.track('Completed Assign to Multiple', {
      numberOfAssignments: selectedUsers.length,
      isGroupWorkspaceItem: workspace.isGroup,
    });

    const duplicateItemParams = {
      itemUuid,
      fromWorkspaceId: workspaceId,
      comment: isEmptyEditor(editorState) ? null : editorStateToJsonString(editorState),
      toWorkspaceIds: [],
      assignToOtherUser: false,
      duplicateWithinWorkspaceUserIds: null,
    };

    // If we are in a group, we want to duplicate all of the items into the current workspace
    // If in a 1:1, then we need to move the items into the 1:1 between the current user
    // and the assigned user
    if (workspace.isGroup) {
      duplicateItemParams.toWorkspaceIds = [workspaceId];
      duplicateItemParams.duplicateWithinWorkspaceUserIds = selectedUsers;
    } else {
      duplicateItemParams.duplicateWithinWorkspaceUserIds = workspace.users
        .filter(u => selectedUsers.includes(u.id))
        .map(u => u.id);
      duplicateItemParams.toWorkspaceIds = selectedUsers.map(userId => oneOnOneWorkspaceIdsByUser[userId]);
      duplicateItemParams.assignToOtherUser = true;
    }

    dispatch(duplicateItem(duplicateItemParams))
      .then(() => {
        WpToastManager.success(tr('perform.action_items.assign_duplicate_modal.toast_success'));
        setLoading(false);
        onToggle();
      })
      .catch(() => {
        WpToastManager.danger(tr('perform.action_items.assign_duplicate_modal.toast_error'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const baseClassName = 'AssignDuplicateModal';

  return (
    <WpModal isOpen onToggle={onToggle} zIndex={1061}>
      <WpModalHeader>{tr('perform.action_items.assign_duplicate_modal.title')}</WpModalHeader>
      <WpModalBody>
        <div className={baseClassName}>
          {isOneOnOneWorkspace && (
            <div className={`${baseClassName}__description`}>
              {otherUserName && tr('perform.action_items.assign_duplicate_modal.description_with_name', otherUserName)}
              {!otherUserName && tr('perform.action_items.assign_duplicate_modal.description_without_name')}
            </div>
          )}
          <div className={`${baseClassName}__user`}>
            <WpCheckbox
              id="select-all"
              label={tr('perform.action_items.assign_duplicate_modal.select_all')}
              checked={selectedUsers.length == userOptions.length}
              onChange={handleSelectAll}
            />
          </div>
          {userOptions.map(user => (
            <div className={`${baseClassName}__user`} onClick={() => toggleUser(user.id)}>
              <WpCheckbox id={user.id} label="" checked={selectedUsers.includes(user.id)} />
              <div className={`${baseClassName}__avatar`}>
                <WpAvatar avatar={user.avatar} size="sm" />
              </div>
              {getFullNameOrEmail(user)}
            </div>
          ))}
          <div className={`${baseClassName}__comment border p-2 rounded`} onClick={focusTextEditor}>
            <DraftEditor
              spellCheck
              editorRef={draftEditorEl}
              placeholder={tr('perform.action_items.assign_duplicate_modal.comment_placeholder')}
              editorState={editorState}
              onChange={setEditorState}
            />
          </div>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={onToggle}>
            {tr('perform.common.cancel')}
          </WpButton>
          <WpButton loading={loading} btnType="submit" onClick={handleSubmit} disabled={selectedUsers.length == 0}>
            {selectedUsers.length > 1
              ? tr('perform.action_items.assign_duplicate_modal.assign_plural', String(selectedUsers.length))
              : tr('perform.action_items.assign_duplicate_modal.assign_singular', String(selectedUsers.length))}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default AssignDuplicateModal;
